import React, { useEffect, useState, useContext, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
} from "@stripe/react-connect-js";
import EMPLoader from "../../../shared/components/EMPLoader/EMPLoader";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import { useStripeOnboarding } from "../../../shared/hooks/useStripeOnboarding";
import MenuBook from "../../../shared/Icons/MenuBook";
import { ModalContext } from "../../../shared/components/EMPModal/ModalContext";
import ChecklistPDF from "../../../onboarding/ChecklistPDF";
import PDF from "../../../shared/content/stripe-job-aid-compressed.pdf";
import {
  trackOnboardingOnClickEvent,
  trackOnClickEvent,
} from "../../../shared/services/analytics/AnalyticsService";
import { useFieldSupportStatus } from "../../../shared/hooks/useFieldSupportStatus";
import StripeComplete from "../../../onboarding/stripe/StripeComplete";
import StripeOnboardingLoadingState from "../../../onboarding/stripe/StripeOnboardingLoadingState";
import StripeOnboardingErrorState from "../../../onboarding/stripe/StripeOnboardingErrorState";
import styles from "./OnboardingPaymentProcessorOnlineDeposits.module.scss";

const I18N_BASE =
  "DealerOnboarding.content.paymentProcessorOnlineDeposits.stripeSetup";

const DealerChecklistLink = ({ t, handleModal, user }) => {
  const openChecklist = (event) => {
    event.preventDefault();
    if (user) {
      trackOnClickEvent(
        "fv:emp:onboarding:stripe setup",
        "emp:onboarding:stripe setup:content:stripe account setup checklist",
        "fv:emp:onboarding:stripe setup:content action",
        user,
        "emp:onboarding"
      );
    }
    handleModal(t(`${I18N_BASE}.checklistButton`), <ChecklistPDF file={PDF} />);
  };

  return (
    <Box className={styles.enrollmentChecklistRow}>
      <MenuBook className={styles.enrollmentChecklistIcon} />
      <Typography component="p" className={styles.enrollmentChecklistText}>
        {t(`${I18N_BASE}.accountNotFound.checklistPrefix`)}
        <a
          className={styles.enrollmentChecklistLink}
          href={t(`${I18N_BASE}.accountNotFound.checklistLink`)}
          onClick={openChecklist}
          data-testid="stripe-account-setup-checklist-link"
        >
          {t(`${I18N_BASE}.accountNotFound.checklistLabel`)}
        </a>
      </Typography>
    </Box>
  );
};

DealerChecklistLink.propTypes = {
  t: PropTypes.func.isRequired,
  handleModal: PropTypes.func.isRequired,
  user: PropTypes.object,
};

/**
 * Ecomm dealer-onboarding Stripe enrollment (create account + embedded Connect).
 * Kept separate from NVC StripeOnboardingEmbedded so that experience is unchanged.
 */
const DealerStripeEnrollment = ({
  user,
  onAccountReady,
  onAccountStatusChange,
  onCreateAccountStart,
  onEditPreferences,
  showEditPreferences,
  stripeUrl,
}) => {
  const { t } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);
  const [isExitingStripe, setIsExitingStripe] = useState(false);
  const { handleModal } = useContext(ModalContext);
  const isFieldSupport = useFieldSupportStatus();
  const accountReadyReportedRef = useRef(false);

  const {
    errorMessage,
    isLoading,
    stripeConnectInstance,
    isAccountEnabled,
    isReady,
    handleCreateAccount,
    handleInitializeStripe,
    detailsSubmitted,
    statusMessage,
    accountId,
    createdTimestamp,
  } = useStripeOnboarding();

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    handleInitializeStripe();
  }, [handleInitializeStripe]);

  useEffect(() => {
    sessionStorage.setItem("stripe.onboardingViewed", "true");
  }, []);

  // Fires once, as soon as the Stripe account exists and the merchant has
  // submitted Stripe's own onboarding form - not once Stripe finishes
  // reviewing it (isAccountEnabled). Completing our "Stripe preference"
  // substep shouldn't be blocked on Stripe's own review turnaround.
  useEffect(() => {
    if (
      !onAccountReady ||
      !detailsSubmitted ||
      !accountId ||
      accountReadyReportedRef.current
    ) {
      return;
    }
    accountReadyReportedRef.current = true;
    onAccountReady({ accountId, createdTimestamp });
  }, [onAccountReady, detailsSubmitted, accountId, createdTimestamp]);

  // Unlike onAccountReady, this reports live status on every change (not
  // just once) so the parent knows when Stripe's own review eventually
  // finishes, even though our substep already completed earlier.
  useEffect(() => {
    onAccountStatusChange?.({ isAccountEnabled, detailsSubmitted });
  }, [onAccountStatusChange, isAccountEnabled, detailsSubmitted]);

  const handleCreateAccountClick = () => {
    onCreateAccountStart?.();
    trackOnboardingOnClickEvent(
      "fv:emp:onboarding:stripe setup",
      "emp:onboarding:stripe setup:content:create stripe account",
      "fv:emp:onboarding:stripe setup:content action",
      user,
      "stripe onboarding start"
    );
    handleCreateAccount();
  };

  if (!isMounted || isLoading || isExitingStripe) {
    return (
      <StripeOnboardingLoadingState
        isExitingStripe={isExitingStripe}
        testId="stripe-enrollment-loading"
      />
    );
  }

  if (errorMessage && errorMessage !== "ACCOUNT_NOT_FOUND") {
    return (
      <StripeOnboardingErrorState t={t} onRetry={handleInitializeStripe} />
    );
  }

  const handleOnboardingExit = (accountObj) => {
    trackOnboardingOnClickEvent(
      "fv:emp:onboarding:stripe setup",
      "emp:onboarding:stripe setup:content:submit",
      "fv:emp:onboarding:stripe setup:content action",
      user,
      "stripe onboarding complete"
    );

    console.log("The account has exited onboarding", accountObj);
    setIsExitingStripe(true);
    setTimeout(() => {
      handleInitializeStripe().finally(() => {
        setIsExitingStripe(false);
      });
    }, 5000);
  };

  if (
    errorMessage === "ACCOUNT_NOT_FOUND" ||
    (!stripeConnectInstance && !isAccountEnabled)
  ) {
    return (
      <Box
        className={styles.stripeEnrollmentContainer}
        data-testid="stripe-account-not-found"
      >
        <Typography
          className={styles.copy}
          data-testid="stripe-account-not-found-copy"
        >
          {t(`${I18N_BASE}.accountNotFound.copy`)}
        </Typography>
        <DealerChecklistLink t={t} handleModal={handleModal} user={user} />
        <Box className={styles.saveButtonContainer}>
          <EMPButton
            variant="contained"
            onClick={handleCreateAccountClick}
            disabled={isLoading || isFieldSupport}
            testId="create-stripe-account-button"
            className={styles.saveButton}
          >
            {t(`${I18N_BASE}.accountNotFound.createAccount`)}
          </EMPButton>
        </Box>
      </Box>
    );
  }

  if (detailsSubmitted) {
    if (isAccountEnabled) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "120px",
          }}
          data-testid="stripe-enrollment-connecting"
        >
          <EMPLoader loadingMessage="Updating Stripe status" />
        </Box>
      );
    }

    return (
      <StripeComplete
        statusMessage={statusMessage}
        accountId={accountId}
        stripeConnectInstance={stripeConnectInstance}
        createdTimestamp={createdTimestamp}
        isReady={isReady}
        user={user}
        onEditPreferences={onEditPreferences}
        showEditPreferences={showEditPreferences}
        stripeUrl={stripeUrl}
      />
    );
  }

  return (
    <Box data-testid="stripe-embedded-onboarding-wrapper">
      <DealerChecklistLink t={t} handleModal={handleModal} user={user} />
      <Box
        sx={{
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          p: 4,
          mb: 4,
          minHeight: "200px",
        }}
        data-testid="stripe-embedded-onboarding"
      >
        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
          <ConnectAccountOnboarding
            onExit={handleOnboardingExit}
            onStepChange={(stepChange) => {
              console.log(`User entered: ${stepChange.step}`);
            }}
          />
        </ConnectComponentsProvider>
      </Box>
    </Box>
  );
};

DealerStripeEnrollment.propTypes = {
  user: PropTypes.object,
  onAccountReady: PropTypes.func,
  onAccountStatusChange: PropTypes.func,
  onCreateAccountStart: PropTypes.func,
  onEditPreferences: PropTypes.func,
  showEditPreferences: PropTypes.bool,
  stripeUrl: PropTypes.string,
};

DealerStripeEnrollment.defaultProps = {
  user: null,
  onAccountReady: undefined,
  onAccountStatusChange: undefined,
  onCreateAccountStart: undefined,
  onEditPreferences: undefined,
  showEditPreferences: true,
  stripeUrl: undefined,
};

export default DealerStripeEnrollment;
